import { GenericService } from '../../../../core/services/GenericService';
import { Injectable } from '@angular/core';
import { ApiService } from '../../../../core/services';
import { IDeliveryOrder } from '../interfaces/IDeliveryOrder';
import { Observable } from 'rxjs';
import { ISimpleListItem } from '../../../../core/interfaces/ISimpleListItem';
import { HttpParams } from '@angular/common/http';
import { IDeliveryOrderProduct } from '../interfaces/IDeliveryOrderProduct';
import { ITableResults } from '../../../../core/interfaces';
import { IOrderChangeLog } from '../interfaces/IOrderChangeLog';
import { IOrderMail, IOrderMailPreview } from '../interfaces/IOrderMail';
import { HttpUtils } from '../../../../core/utils/HttpUtils';

@Injectable({
  providedIn: 'root'
})
export class OrdersService extends GenericService<IDeliveryOrder> {
  protected readonly baseUrl = '/orders';

  constructor(public apiService: ApiService) {
    super(apiService);
  }

  getArticlesSimpleList(
    search = '',
    onlyNonPacking: boolean,
    supplierId?: number,
    onlyProductionArticles = false,
    requireSupplier = false,
    includeAssigned = false,
    results?: number
  ): Observable<ISimpleListItem[]> {
    let params = new HttpParams()
      .append('search', `${search}`)
      .append('onlyNonPacking', onlyNonPacking)
      .append('results', `${results ?? 50}`);

    if (onlyProductionArticles) {
      params = params.append('onlyProductionArticles', true);
    }

    if (supplierId) {
      params = params.append('supplierId', supplierId);
    }

    if (requireSupplier) {
      params = params.append('requireSupplier', true);
    }

    if (includeAssigned) {
      params = params.append('includeAssigned', true);
    }

    return this.apiService.get(`${this.baseUrl}/articles/simple`, params);
  }

  bookProductOrder(
    orderProduct: IDeliveryOrderProduct,
    extra: { version?: number; idempotencyKey: string }
  ): Observable<IDeliveryOrder> {
    return this.apiService.post(`${this.baseUrl}/products/${orderProduct.id.toString(10)}/book`, {
      ...orderProduct,
      ...extra,
    });
  }

  bookAllOutstanding(
    orderId: number,
    body: { deliveryDate: string; mhd?: string; charge?: string; version?: number; idempotencyKey: string }
  ): Observable<IDeliveryOrder> {
    return this.apiService.post(`${this.baseUrl}/${orderId}/book-all`, body);
  }

  correctProductOrder(
    orderProductId: number,
    body: { deliveredQuantity: number; reason: string; version?: number; idempotencyKey: string }
  ): Observable<IDeliveryOrder> {
    return this.apiService.post(`${this.baseUrl}/products/${orderProductId}/correct`, body);
  }

  cancelProductOrder(id: number): Observable<null> {
    return this.apiService.post(`${this.baseUrl}/products/${id}/cancel` );
  }

  cancel(id: number): Observable<null> {
    return this.apiService.post(`${this.baseUrl}/${id}/cancel` );
  }

  public changeOrderStatus(orderId: number, status: string): Observable<void> {
    return this.apiService.post(`${this.baseUrl}/${orderId}/change-order-status`, { status });
  }

  getOrderMailPreview(orderId: number): Observable<IOrderMailPreview> {
    return this.apiService.get(`${this.baseUrl}/${orderId}/mail-preview`);
  }

  getOrderMails(orderId: number): Observable<IOrderMail[]> {
    return this.apiService.get(`${this.baseUrl}/${orderId}/mails`);
  }

  sendOrderMail(orderId: number, body: { cc: string[]; body: string }): Observable<IOrderMail> {
    return this.apiService.post(`${this.baseUrl}/${orderId}/send-mail`, body);
  }

  getQueryableOrderChangeLogByOrderId(
    orderId: number,
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filters: Array<{ key: string; value: string[] }>
  ): Observable<ITableResults<IOrderChangeLog>> {
    const params = HttpUtils.prepareQueryableParams(pageIndex, pageSize, sortField, sortOrder, filters, null, []);
    return this.apiService.get(`${this.baseUrl}/${orderId}/change-logs`, params);
  }
}
